<template>
  <div
    v-clickoutside="() => isEdit = false"
    :class="{
      'text-date--editable': isEditable,
    }"
    class="text-date">
    <div
      v-show="!isEdit"
      class="text-date__info-wrapper">
      <p
        class="text-date__info"
        @click="showEditableTemplate">
        {{ currentDateString }}
      </p>

      <error-tooltip
        :error="error"
        :error-triggers="errorTriggers"
        :z-index="zIndexTooltip" />
    </div>

    <template v-if="isEdit">
      <ui-input
        v-if="showDays"
        v-model.number="editableDays"
        :text-appendix="{
          prefix: 'days |',
        }"
        class="text-date__input" />

      <ui-input
        v-model.number="editableHours"
        :text-appendix="{
          prefix: 'hours |',
        }"
        class="text-date__input" />

      <ui-input
        v-model.number="editableMinutes"
        :text-appendix="{
          prefix: 'minutes |',
        }"
        class="text-date__input" />

      <button
        type="button"
        class="button button-main text-date__button"
        @click="saveTime">
        Save
      </button>
    </template>
  </div>
</template>

<script>
import { MS_PER_DAY, MS_PER_HOUR, MS_PER_MINUTE } from 'Root/helpers/DateConstants';
import clickoutside from 'Root/directives/clickoutside';
import UiInput from 'Components/ui/Input/UiInput.vue';
import ErrorTooltip from 'Components/ui/Tooltip/ErrorTooltip.vue';

export default {
  name: 'TextDate',

  directives: {
    ...clickoutside,
  },

  components: {
    ErrorTooltip,
    UiInput,
  },

  props: {
    value: {
      type: Number, // 435720000
      required: true,
    },

    isShortNames: {
      type: Boolean,
      default: false,
    },

    isEditable: {
      type: Boolean,
      default: false,
    },

    error: {
      type: [String, Boolean],
      default: null,
    },

    errorTriggers: {
      type: Array,
      default: () => (['hover', 'manual', 'click']),
    },

    zIndexTooltip: {
      type: Number,
      default: 100,
    },

    showDays: {
      type: Boolean,
      default: true,
    },

    useSeconds: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      isEdit: false,
      days: 0,
      hours: 0,
      minutes: 0,
    };
  },

  computed: {
    dateToTime() {
      const valueInMs = this.useSeconds ? this.value * 1000 : this.value;
      return {
        days: Math.floor(valueInMs / MS_PER_DAY),
        hours: Math.floor((valueInMs % MS_PER_DAY) / MS_PER_HOUR),
        minutes: Math.floor((valueInMs % MS_PER_HOUR) / MS_PER_MINUTE),
      };
    },

    editableDays: {
      get() {
        return this.days;
      },

      set(value) {
        this.days = value <= 100 ? value : '100';
      },
    },

    editableHours: {
      get() {
        return this.hours;
      },

      set(value) {
        this.hours = value <= 23 ? value : '23';
      },
    },

    editableMinutes: {
      get() {
        return this.minutes;
      },

      set(value) {
        this.minutes = value <= 59 ? value : '59';
      },
    },

    currentDate() {
      const daysInMs = this.showDays ? MS_PER_DAY * this.days : 0;
      const totalMs = daysInMs + MS_PER_HOUR * this.hours + MS_PER_MINUTE * this.minutes;
      return this.useSeconds ? totalMs / 1000 : totalMs;
    },

    currentDateString() {
      if (this.dateToTime.days > 0) {
        // округляем вверх
        const value = this.dateToTime.days + Number(this.dateToTime.hours > 0 || this.dateToTime.minutes > 0);

        return `${value} ${this.setLocale(value, 'days')}`;
      }

      if (this.dateToTime.hours > 0) {
        // округляем вверх
        const value = this.dateToTime.hours + Number(this.dateToTime.minutes > 0);

        return `${value} ${this.setLocale(value, 'hours')}`;
      }

      const { minutes } = this.dateToTime;

      return `${minutes} ${this.setLocale(minutes, 'minutes')}`;
    },
  },

  watch: {
    isEdit() {
      this.days = this.dateToTime.days;
      this.hours = this.dateToTime.hours;
      this.minutes = this.dateToTime.minutes;
    },
  },

  methods: {
    saveTime() {
      this.isEdit = false;

      this.$emit('input', this.currentDate);
    },

    showEditableTemplate() {
      if (this.isEditable) {
        this.isEdit = true;
      }
    },

    setLocale(value, duration) {
      // eslint-disable-next-line no-nested-ternary
      return this.isShortNames
        ? duration.charAt(0)
        : value === 1
          ? duration.slice(0, -1)
          : duration;
    },
  },
};
</script>

<style scoped lang="scss">
.text-date, .text-date__info-wrapper {
  display: inline-grid;
  align-items: center;
  grid-gap: 8px;
}

.text-date--editable {
  .text-date__info {
    cursor: pointer;
    border-bottom: 1px dashed currentColor;
  }
}

.text-date__info-wrapper {
  grid-auto-flow: column;
}

.text-date__info {
  grid-column: 1 / -1;
  color: $color--basic-white;
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
}

.text-date__input {
  --height: 40px;
  --font-size: 13px;
  --font-weight: 500;
  --border-radius: 4px;
  --prefix-font-size: 11px;
  --prefix-text-transform: uppercase;
  --prefix-margin: 0 4px 0 0;
  --prefix-color: #{$color--basic-dark-gray};
  --input-min-width: 25px;
  --padding: 0 12px;
}

.text-date__button {
  padding: 12px;
  border-radius: 4px;
}

@media only screen and #{$screen-more-or-1024} {
  .text-date__info {
    font-size: 15px;
  }
}
</style>
